/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Grid, MenuItem, FormHelperText } from '@mui/material';
import type { Polygon } from 'geojson';
import type { TFunction } from 'i18next';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  isValidGeoJsonCoordinates,
  isMaximumOneDrawing,
  hasIntersectionWithFIR,
  isInteger,
  ReactHookFormSelect,
  ReactHookFormRadioGroup,
  ReactHookFormNumberField,
  ReactHookFormHiddenInput,
  isValidMax,
  isValidMin,
  isEmpty,
  hasMulitpleIntersections,
  useDraftFormHelpers,
  countIntersectionPoints,
} from '@opengeoweb/form-fields';
import type { DrawMode } from '@opengeoweb/webmap-react';
import DrawTools from './DrawTools';
import type { ConfigurableFormFieldProps, ProductConfig } from '../../../types';
import { Direction, MovementUnit } from '../../../types';
import { styles } from '../ProductForm.styles';
import { RadioButtonAndLabel } from './RadioButtonAndLabel';
import {
  getActiveFIRArea,
  getAllowedUnits,
  getMaxMovementSpeedValue,
  getMinMovementSpeedValue,
  isFir,
  triggerValidations,
  hasMaxFeaturePoints,
  getMaxPolygonPoints,
} from '../utils';
import ProductFormFieldLayout from './ProductFormFieldLayout';
import { useShortTestHelpers } from '../../../hooks/useShortTestHelpers';
import { useSigmetAirmetTranslation } from '../../../utils/i18n';

export const DEFAULT_ROUNDING_MOVEMENT_KT = 5;
export const DEFAULT_ROUNDING_MOVEMENT_KMH = 10;

export const getMovementStepValKTMessage = (
  t: TFunction,
  value = DEFAULT_ROUNDING_MOVEMENT_KT,
): string =>
  t('progress-get-movement-step-valktmessage', {
    value,
  });
export const getMovementStepValKMHMessage = (
  t: TFunction,
  value = DEFAULT_ROUNDING_MOVEMENT_KMH,
): string =>
  t('progress-get-movement-step-valkmhmessage', {
    value,
  });

export const validateMovementSteps = (
  t: TFunction,
  value: string,
  unit: string,
  productConfig: ProductConfig,
  selectedFIR: string,
): boolean | string => {
  if (isEmpty(value)) {
    return true;
  }

  const {
    /* eslint-disable @typescript-eslint/naming-convention */
    movement_rounding_kt = DEFAULT_ROUNDING_MOVEMENT_KT,
    movement_rounding_kmh = DEFAULT_ROUNDING_MOVEMENT_KMH,
    /* eslint-enable @typescript-eslint/naming-convention */
  } = getActiveFIRArea(selectedFIR, productConfig);

  // Parse to integer to check for steps
  const intLevel = parseInt(value, 10);

  if (MovementUnit[unit as keyof typeof MovementUnit] === MovementUnit.KT) {
    return (
      intLevel % movement_rounding_kt === 0 ||
      getMovementStepValKTMessage(t, movement_rounding_kt)
    );
  }
  if (MovementUnit[unit as keyof typeof MovementUnit] === MovementUnit.KMH) {
    return (
      intLevel % movement_rounding_kmh === 0 ||
      getMovementStepValKMHMessage(t, movement_rounding_kmh)
    );
  }
  return t('invalid-unit');
};

interface ProgressProps extends ConfigurableFormFieldProps {
  geoJSON?: GeoJSON.FeatureCollection;
  geoJSONIntersection?: GeoJSON.FeatureCollection;
  activeTool?: string;
  tools?: DrawMode[];
  onChangeTool?: (newToolMode: DrawMode) => void;
  drawToolsDisabled?: boolean;
}

const Progress: React.FC<ProgressProps> = ({
  productType,
  productConfig,
  isDisabled = false,
  isReadOnly = false,
  onChange = (): void => {},
  tools = [],
  activeTool,
  onChangeTool = (): void => {},
  geoJSON,
  geoJSONIntersection,
  drawToolsDisabled = false,
}: ProgressProps) => {
  const { t } = useSigmetAirmetTranslation();
  const {
    watch,
    getValues,
    trigger,
    formState: { errors },
    unregister,
  } = useFormContext();
  const { isRequired: isRequiredField, isDraft } = useDraftFormHelpers();
  const { isShortTest } = useShortTestHelpers();
  const isRequired = (value: string): string | boolean =>
    isShortTest() || isRequiredField(value);

  const shortTest = isShortTest();
  const combinedDisabled = shortTest || drawToolsDisabled;

  const disabledTooltip =
    !shortTest && drawToolsDisabled
      ? t('geometry-drawing-disabled')
      : undefined;

  const movementType = watch('movementType');
  const getFIRValue = (): string =>
    getValues('locationIndicatorATSR') as string;

  // Get allowed movement units based on selected FIR - if no FIR selected, allow all units
  const allowedMovementUnitsForFir = getAllowedUnits(
    getFIRValue(),
    productConfig,
    'movement_unit',
    MovementUnit,
  );

  React.useEffect(() => {
    if (movementType !== 'MOVEMENT') {
      unregister('movementDirection');
      unregister('movementSpeed');
      unregister('movementUnit');
    }

    if (movementType !== 'FORECAST_POSITION') {
      unregister('endGeometry');
      unregister('endGeometryIntersect');
    }
  }, [movementType, unregister]);

  const isSigmet = productType === 'sigmet';
  const isRadioactiveCloud = watch('phenomenon') === 'RDOACT_CLD';
  const isVolcanicCloud = watch('phenomenon') === 'VA_CLD';
  const isShortTestOrRdoact = isShortTest() || isRadioactiveCloud;
  const fieldsDisabled = isDisabled || isShortTestOrRdoact;

  // Max points the FIR allows for a polygon
  const maxPoints = getMaxPolygonPoints(getFIRValue(), productConfig);

  // Intersections with FIR
  const intersectionPointCount = countIntersectionPoints(
    geoJSON! as GeoJSON.FeatureCollection<Polygon>,
    geoJSONIntersection! as GeoJSON.FeatureCollection<Polygon>,
  );
  const drawnGeometry = geoJSON?.features[0]?.geometry as GeoJSON.Polygon;
  const interceptionGeometry = geoJSONIntersection?.features[0]
    ?.geometry as GeoJSON.Polygon;

  // Total points in the user drawn polygon
  const totalDrawnPoints = drawnGeometry?.coordinates[0]
    ? drawnGeometry.coordinates[0].length
    : 0;

  // Total points in the intersection polygon
  const intersectingPolygonPointsTotal = interceptionGeometry?.coordinates[0]
    ? interceptionGeometry.coordinates[0].length
    : 0;

  const showMaxFeaturePointsWarning =
    intersectionPointCount > 7 &&
    hasMaxFeaturePoints(geoJSONIntersection!, maxPoints) &&
    !activeTool &&
    !errors.startGeometry &&
    !isFir(geoJSON!);

  const showMoreThan6PointsWarning =
    totalDrawnPoints > 7 &&
    intersectionPointCount < 1 &&
    !activeTool &&
    !errors.startGeometry &&
    !isFir(geoJSON!);

  const showIntersectionAbove6PointsWarning =
    intersectingPolygonPointsTotal > 7 &&
    intersectionPointCount > 1 &&
    maxPoints > 7 &&
    !activeTool &&
    !errors.startGeometry &&
    !isFir(geoJSON!);

  return (
    <ProductFormFieldLayout sx={styles.containerItem} title={t('progress')}>
      <ReactHookFormRadioGroup
        data-testid="movementType-group"
        isReadOnly={isReadOnly}
        name="movementType"
        onChange={(): void => {
          const deleteTool = tools?.find((tool) => tool.value === 'DELETE');
          if (deleteTool) {
            onChangeTool(deleteTool);
          }
        }}
        rules={{ validate: { isRequired } }}
      >
        {!!isVolcanicCloud && (
          <RadioButtonAndLabel
            data-testid="movementType-NO_VA_EXP"
            disabled={fieldsDisabled}
            label={t('progress-radio-button-label-no-va-exp')}
            value="NO_VA_EXP"
          />
        )}
        <>
          <RadioButtonAndLabel
            data-testid="movementType-STATIONARY"
            disabled={fieldsDisabled}
            label={t('progress-radio-button-label-stationary')}
            value="STATIONARY"
          />
          <RadioButtonAndLabel
            data-testid="movementType-MOVEMENT"
            disabled={fieldsDisabled}
            label={t('progress-radio-button-label-movement')}
            value="MOVEMENT"
          />
          {!!isSigmet && !!isShortTestOrRdoact && (
            <RadioButtonAndLabel
              data-testid="movementType-FORECAST_POSITION"
              disabled={fieldsDisabled}
              label={t('progress-radio-button-label-end-potision')}
              value="FORECAST_POSITION"
            />
          )}
        </>
        {movementType === 'MOVEMENT' && !isShortTestOrRdoact && (
          <>
            <Grid>
              <ReactHookFormSelect
                autoFocus
                data-testid="movement-movementDirection"
                disabled={isDisabled}
                isReadOnly={isReadOnly}
                label={t('progress-movement-direction-label')}
                name="movementDirection"
                onChange={(event): void => {
                  event.stopPropagation();
                  onChange();
                }}
                rules={{ validate: { isRequired } }}
                size="small"
                sx={styles.movement}
              >
                {Object.keys(Direction).map((key) => (
                  <MenuItem key={key} value={key}>
                    {Direction[key as keyof typeof Direction]}
                  </MenuItem>
                ))}
              </ReactHookFormSelect>
            </Grid>
            <Grid container>
              <Grid size={5}>
                <ReactHookFormSelect
                  data-testid="movement-movementUnit"
                  defaultValue={'KT' as MovementUnit}
                  disabled={isDisabled}
                  isReadOnly={isReadOnly}
                  label={t('progress-movement-unit-label')}
                  name="movementUnit"
                  onChange={(event): void => {
                    event.stopPropagation();
                    triggerValidations(['movementSpeed'], getValues, trigger);
                    onChange();
                  }}
                  rules={{ validate: { isRequired } }}
                  size="small"
                  sx={styles.unit}
                >
                  {Object.keys(allowedMovementUnitsForFir).map((key) => (
                    <MenuItem key={key} value={key}>
                      {MovementUnit[key as keyof typeof MovementUnit]}
                    </MenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid size={7}>
                <ReactHookFormNumberField
                  data-testid="movement-movementSpeed"
                  disabled={isDisabled}
                  isReadOnly={isReadOnly}
                  label={t('progress-movement-speed-label')}
                  name="movementSpeed"
                  onChange={(event): void => event?.stopPropagation()}
                  rules={{
                    validate: {
                      isRequired,
                      isInteger,
                      min: (value): boolean | string =>
                        // The max level depends on the unit
                        isValidMin(
                          value,
                          getMinMovementSpeedValue(
                            getValues('movementUnit'),
                            getFIRValue(),
                            productConfig,
                          ),
                        ) ||
                        t('progress-the-minimum-level-in-movement', {
                          movementUnitType:
                            MovementUnit[
                              getValues(
                                'movementUnit',
                              ) as keyof typeof MovementUnit
                            ],
                          movementUnit: getMinMovementSpeedValue(
                            getValues('movementUnit'),
                            getFIRValue(),
                            productConfig,
                          ),
                        }),

                      max: (value): boolean | string =>
                        // The max level depends on the unit
                        isValidMax(
                          value,
                          getMaxMovementSpeedValue(
                            getValues('movementUnit'),
                            getFIRValue(),
                            productConfig,
                          ),
                        ) ||
                        t('progress-the-maxium-level-in-movement', {
                          movementUnitType:
                            MovementUnit[
                              getValues(
                                'movementUnit',
                              ) as keyof typeof MovementUnit
                            ],
                          movementUnit: getMaxMovementSpeedValue(
                            getValues('movementUnit'),
                            getFIRValue(),
                            productConfig,
                          ),
                        }),

                      validateMovementSteps: (value): boolean | string =>
                        // movement step depends on the unit
                        validateMovementSteps(
                          t,
                          value,
                          getValues('movementUnit'),
                          productConfig,
                          getFIRValue(),
                        ),
                    },
                  }}
                  size="small"
                />
              </Grid>
            </Grid>
          </>
        )}
        {!!isSigmet && !isShortTestOrRdoact && (
          <RadioButtonAndLabel
            data-testid="movementType-FORECAST_POSITION"
            disabled={isDisabled}
            label={t('progress-radio-button-label-end-potision')}
            value="FORECAST_POSITION"
          />
        )}
      </ReactHookFormRadioGroup>
      {!!isSigmet &&
        !isShortTestOrRdoact &&
        movementType === 'FORECAST_POSITION' && (
          <Grid
            data-testid="endGeometry"
            size={12}
            sx={styles.drawSectionProgress}
          >
            {!isReadOnly && (
              <>
                <DrawTools
                  activeTool={activeTool!}
                  data-testid="endGeometry-drawTools"
                  disabledTooltip={disabledTooltip}
                  isDisabled={combinedDisabled}
                  onChangeTool={onChangeTool}
                  tools={tools}
                />
                {!!activeTool && (
                  <FormHelperText
                    sx={styles.quitDrawModeMessage}
                    variant="filled"
                  >
                    {t('geometry-exit-draw-mode-message')}
                  </FormHelperText>
                )}
                {!!errors.endGeometry && (
                  <FormHelperText error variant="filled">
                    {errors.endGeometry.message as string}
                  </FormHelperText>
                )}
                {
                  /* non-blocking warnings */
                  !!showMaxFeaturePointsWarning && (
                    <FormHelperText variant="filled">
                      {t('geometry-max-feature-points-message')}
                    </FormHelperText>
                  )
                }
                {
                  /* non-blocking warnings */
                  !!showMoreThan6PointsWarning && (
                    <FormHelperText variant="filled">
                      {t('progress-more-than-6-points-message', {
                        totalDrawnPoints,
                      })}
                    </FormHelperText>
                  )
                }
                {
                  /* non-blocking warnings */
                  !!showIntersectionAbove6PointsWarning && (
                    <FormHelperText variant="filled">
                      {t('progress-intersection-above-6-points-message', {
                        intersectingPolygonPointsTotal,
                      })}
                    </FormHelperText>
                  )
                }
                {hasMulitpleIntersections(geoJSONIntersection!) &&
                  !activeTool &&
                  !errors.endGeometry &&
                  !isFir(geoJSON!) && (
                    <FormHelperText variant="filled">
                      {t('geometry-multi-intersections-message')}
                    </FormHelperText>
                  )}
              </>
            )}
            <ReactHookFormHiddenInput
              name="endGeometry"
              rules={{
                validate: {
                  maximumOneDrawing: (
                    value: GeoJSON.FeatureCollection,
                  ): boolean | string =>
                    isMaximumOneDrawing(value) ||
                    t('progress-only-one-position-drawing'),
                  coordinatesNotEmpty: (
                    value: GeoJSON.FeatureCollection,
                  ): boolean | string =>
                    getValues('movementType') === 'FORECAST_POSITION' &&
                    !isDraft()
                      ? isValidGeoJsonCoordinates(value) ||
                        t('progress-coordinate-empty-message')
                      : true,
                  intersectWithFIR: (
                    value: GeoJSON.FeatureCollection,
                  ): boolean | string =>
                    hasIntersectionWithFIR(
                      value,
                      getValues('endGeometryIntersect'),
                    ) || t('progress-no-intersection-message'),
                  hasMaxFeaturePoints: (
                    value: GeoJSON.FeatureCollection<Polygon>,
                  ): boolean | string =>
                    isFir(value)
                      ? true
                      : !hasMaxFeaturePoints(value, maxPoints) ||
                        t('progress-maximumPointsMessage', {
                          maxPoints: maxPoints - 1,
                        }),
                },
              }}
            />
            <ReactHookFormHiddenInput name="endGeometryIntersect" />
          </Grid>
        )}
    </ProductFormFieldLayout>
  );
};

export default Progress;
